import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Lock, LogOut, CalendarCheck, MessageSquare, Phone, Mail, Car, Calendar,
  RefreshCw, Inbox, CheckCircle2, PhoneCall,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "caliber_admin_token";

const statusStyles = {
  new: "bg-red-100 text-red-700 border-red-200",
  contacted: "bg-amber-100 text-amber-700 border-amber-200",
  done: "bg-emerald-100 text-emerald-700 border-emerald-200",
};

const StatusBadge = ({ status }) => (
  <Badge variant="outline" className={`uppercase text-[10px] font-bold tracking-widest ${statusStyles[status] || statusStyles.new}`}>
    {status}
  </Badge>
);

const StatusButtons = ({ current, onChange }) => (
  <div className="flex gap-2">
    {[
      { value: "new", label: "New", icon: Inbox },
      { value: "contacted", label: "Contacted", icon: PhoneCall },
      { value: "done", label: "Done", icon: CheckCircle2 },
    ].map((s) => (
      <button
        key={s.value}
        onClick={() => onChange(s.value)}
        data-testid={`status-btn-${s.value}`}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wide border transition-colors duration-200 ${
          current === s.value
            ? "bg-zinc-900 text-white border-zinc-900"
            : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"
        }`}
      >
        <s.icon size={13} /> {s.label}
      </button>
    ))}
  </div>
);

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAppointments([]);
    setMessages([]);
  }, []);

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [apptRes, msgRes] = await Promise.all([
        axios.get(`${API}/appointments`, authHeaders()),
        axios.get(`${API}/messages`, authHeaders()),
      ]);
      setAppointments(apptRes.data);
      setMessages(msgRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        logout();
      } else {
        toast.error("Failed to load data. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, [token, authHeaders, logout]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!password.trim()) {
      toast.error("Please enter the password.");
      return;
    }
    setLoggingIn(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
      setPassword("");
      toast.success("Welcome back!");
    } catch (err) {
      toast.error(err.response?.status === 401 ? "Incorrect password." : "Login failed. Please try again.");
    } finally {
      setLoggingIn(false);
    }
  };

  const updateStatus = async (type, id, status) => {
    try {
      await axios.patch(`${API}/${type}/${id}`, { status }, authHeaders());
      if (type === "appointments") {
        setAppointments((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
      } else {
        setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, status } : m)));
      }
      toast.success("Status updated.");
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        logout();
      } else {
        toast.error("Failed to update status.");
      }
    }
  };

  // ---------- LOGIN SCREEN ----------
  if (!token) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-zinc-50 px-6 py-20" data-testid="admin-login-page">
        <div className="w-full max-w-md bg-white rounded-2xl border border-zinc-100 shadow-lg p-10">
          <div className="w-14 h-14 rounded-xl bg-red-600 text-white flex items-center justify-center mb-6">
            <Lock size={24} />
          </div>
          <h1 className="font-heading text-3xl font-extrabold text-zinc-900 mb-2">Admin Login</h1>
          <p className="text-sm text-zinc-500 mb-8">Enter your admin password to view appointment requests and messages.</p>
          <form onSubmit={handleLogin} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="admin-password" className="text-xs font-bold uppercase tracking-wider text-zinc-700">Password</Label>
              <Input
                id="admin-password"
                type="password"
                data-testid="admin-password-input"
                placeholder="Enter admin password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12"
              />
            </div>
            <Button type="submit" disabled={loggingIn} className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold uppercase tracking-wide text-sm h-12 rounded-md transition-all duration-200" data-testid="admin-login-btn">
              {loggingIn ? "Logging In..." : "Log In"}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // ---------- DASHBOARD ----------
  const newAppointments = appointments.filter((a) => a.status === "new").length;
  const newMessages = messages.filter((m) => m.status === "new").length;

  return (
    <div className="bg-zinc-50 min-h-screen py-14" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-10">
          <div>
            <h1 className="font-heading text-4xl font-extrabold text-zinc-900 mb-1.5">Admin Dashboard</h1>
            <p className="text-sm text-zinc-500">
              {newAppointments} new appointment{newAppointments !== 1 ? "s" : ""} &bull; {newMessages} new message{newMessages !== 1 ? "s" : ""}
            </p>
          </div>
          <div className="flex gap-3">
            <Button onClick={fetchData} disabled={loading} variant="outline" className="border-zinc-300 text-zinc-700 hover:border-zinc-500 font-semibold uppercase tracking-wide text-xs h-10 px-5" data-testid="admin-refresh-btn">
              <RefreshCw size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button onClick={logout} variant="outline" className="border-zinc-300 text-zinc-700 hover:border-red-500 hover:text-red-600 font-semibold uppercase tracking-wide text-xs h-10 px-5" data-testid="admin-logout-btn">
              <LogOut size={14} className="mr-1.5" /> Log Out
            </Button>
          </div>
        </div>

        <Tabs defaultValue="appointments">
          <TabsList className="bg-white border border-zinc-200 h-12 p-1 mb-8">
            <TabsTrigger value="appointments" className="data-[state=active]:bg-red-600 data-[state=active]:text-white font-semibold uppercase tracking-wide text-xs px-6 h-9" data-testid="tab-appointments">
              <CalendarCheck size={15} className="mr-2" /> Appointments ({appointments.length})
            </TabsTrigger>
            <TabsTrigger value="messages" className="data-[state=active]:bg-red-600 data-[state=active]:text-white font-semibold uppercase tracking-wide text-xs px-6 h-9" data-testid="tab-messages">
              <MessageSquare size={15} className="mr-2" /> Messages ({messages.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="appointments">
            {appointments.length === 0 ? (
              <div className="bg-white rounded-xl border border-zinc-100 p-14 text-center text-zinc-500" data-testid="appointments-empty">
                <Inbox size={36} className="mx-auto mb-4 text-zinc-300" />
                No appointment requests yet. They'll appear here when customers book online.
              </div>
            ) : (
              <div className="space-y-4">
                {appointments.map((a) => (
                  <div key={a.id} className="bg-white rounded-xl border border-zinc-100 shadow-sm p-6" data-testid={`appointment-row-${a.id}`}>
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3 flex-wrap">
                          <h3 className="font-heading text-xl font-bold text-zinc-900">{a.name}</h3>
                          <StatusBadge status={a.status} />
                          <span className="text-xs text-zinc-400">{formatDate(a.createdAt)}</span>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm text-zinc-700 mb-3">
                          <a href={`tel:${a.phone}`} className="flex items-center gap-2 hover:text-red-600 transition-colors">
                            <Phone size={14} className="text-red-600" /> {a.phone}
                          </a>
                          {a.email && (
                            <a href={`mailto:${a.email}`} className="flex items-center gap-2 hover:text-red-600 transition-colors">
                              <Mail size={14} className="text-red-600" /> {a.email}
                            </a>
                          )}
                          {a.vehicle && (
                            <span className="flex items-center gap-2"><Car size={14} className="text-red-600" /> {a.vehicle}</span>
                          )}
                          <span className="flex items-center gap-2">
                            <CalendarCheck size={14} className="text-red-600" /> {a.service}
                          </span>
                          {a.preferredDate && (
                            <span className="flex items-center gap-2"><Calendar size={14} className="text-red-600" /> Preferred: {a.preferredDate}</span>
                          )}
                        </div>
                        {a.message && (
                          <p className="text-sm text-zinc-600 bg-zinc-50 rounded-lg px-4 py-3 leading-relaxed">{a.message}</p>
                        )}
                        {a.photos && a.photos.length > 0 && (
                          <div className="mt-3">
                            <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-500 mb-2">
                              Damage Photos ({a.photos.length}) — click to enlarge
                            </p>
                            <div className="flex flex-wrap gap-2" data-testid={`appointment-photos-${a.id}`}>
                              {a.photos.map((photoId, idx) => (
                                <a
                                  key={photoId}
                                  href={`${API}/photos/${photoId}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="block w-24 h-24 rounded-lg overflow-hidden border border-zinc-200 hover:border-red-400 hover:shadow-md transition-all duration-200"
                                >
                                  <img
                                    src={`${API}/photos/${photoId}`}
                                    alt={`Damage photo ${idx + 1} from ${a.name}`}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                  />
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="shrink-0">
                        <StatusButtons current={a.status} onChange={(s) => updateStatus("appointments", a.id, s)} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="messages">
            {messages.length === 0 ? (
              <div className="bg-white rounded-xl border border-zinc-100 p-14 text-center text-zinc-500" data-testid="messages-empty">
                <Inbox size={36} className="mx-auto mb-4 text-zinc-300" />
                No contact messages yet. They'll appear here when customers reach out.
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((m) => (
                  <div key={m.id} className="bg-white rounded-xl border border-zinc-100 shadow-sm p-6" data-testid={`message-row-${m.id}`}>
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3 flex-wrap">
                          <h3 className="font-heading text-xl font-bold text-zinc-900">{m.name}</h3>
                          <StatusBadge status={m.status} />
                          <span className="text-xs text-zinc-400">{formatDate(m.createdAt)}</span>
                        </div>
                        <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm text-zinc-700 mb-3">
                          {m.phone && (
                            <a href={`tel:${m.phone}`} className="flex items-center gap-2 hover:text-red-600 transition-colors">
                              <Phone size={14} className="text-red-600" /> {m.phone}
                            </a>
                          )}
                          {m.email && (
                            <a href={`mailto:${m.email}`} className="flex items-center gap-2 hover:text-red-600 transition-colors">
                              <Mail size={14} className="text-red-600" /> {m.email}
                            </a>
                          )}
                        </div>
                        <p className="text-sm text-zinc-600 bg-zinc-50 rounded-lg px-4 py-3 leading-relaxed">{m.message}</p>
                      </div>
                      <div className="shrink-0">
                        <StatusButtons current={m.status} onChange={(s) => updateStatus("messages", m.id, s)} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
